import React, { memo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'

import axios from 'lib/axios'

function UploadContainer({
  children,
  uploadPath = '',
  hideCss = true,
  isDisabled = false,
  onChange = () => null,
  onSuccess = () => null,
  uploadToHomevestData = false,
  makePublic = false
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState(null)
  const [error, setError] = useState(null)
  const [downloadUrl, setDownloadUrl] = useState(null)

  function clearData() {
    if (file) {
      URL.revokeObjectURL(file.preview)
    }
  }

  const resetState = () => {
    setFile(null)
    setIsLoading(false)
    setError(null)
    setDownloadUrl(null)
  }

  useEffect(() => {
    resetState()
    return () => clearData()
  }, [uploadPath])

  useEffect(() => {
    onChange({
      isLoading,
      file,
      error,
      uploadPath,
      downloadUrl,
      resetState
    })
  }, [isLoading, file, error, downloadUrl])

  const onDrop = async (acceptedFiles) => {
    if (isDisabled) {
      return
    }

    resetState()
    setIsLoading(true)

    try {
      const acceptedFile = acceptedFiles[0]

      if (!acceptedFile) {
        return
      }

      setFile(
        Object.assign(acceptedFile, {
          preview: URL.createObjectURL(acceptedFile)
        })
      )

      if (uploadPath) {
        const params = {
          is_admin: true,
          file_name: uploadPath
        }
        if (uploadToHomevestData) {
          params.is_homevest_data = uploadToHomevestData
        }
        if (makePublic) {
          params.make_public = makePublic
        }
        const { data } = await axios.get('documents/signed_upload_url', { params })

        const headers = { 'Content-Type': acceptedFile.type }
        if (makePublic) {
          headers['x-goog-acl'] = 'public-read'
        }
        await axios.put(data.upload_url, acceptedFile, { headers })

        const downloadUrl = data.download_url

        setDownloadUrl(downloadUrl)
        onSuccess(downloadUrl)
      }
    } catch (err) {
      setDownloadUrl(null)
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dropzone disabled={isDisabled} multiple={false} onDrop={onDrop}>
      {({ getRootProps, getInputProps, isDragActive }) => {
        return (
          <div
            {...getRootProps()}
            className={hideCss ? '' : `dropzone ${isDragActive ? 'dropzone--isActive' : ''}`}
          >
            <input {...getInputProps()} />
            {React.cloneElement(children, {
              isLoading,
              downloadUrl,
              remove: resetState,
              error
            })}
          </div>
        )
      }}
    </Dropzone>
  )
}

UploadContainer.propTypes = {
  children: PropTypes.node,
  hideCss: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  uploadPath: PropTypes.string.isRequired
}

export default memo(UploadContainer)
